import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkListPage from '../../../components/work/WorkListPage';
import StandardFilterPanel from '../../../components/hr/StandardFilterPanel';
import ProjectCalendarView from '../../../components/work/ProjectCalendarView';
import { projectsApi } from '../../../utils/workApi';
import { confirmWorkDelete } from '../../../utils/workConfirmModal';
import {
  Button,
  Table,
  Tag,
  Progress,
  Space,
  message,
  Dropdown,
  Tooltip,
  Avatar,
  Select,
  Segmented,
  Modal,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  ProjectOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  MoreOutlined,
  PushpinOutlined,
  InboxOutlined,
  BarChartOutlined,
  CalendarOutlined,
  FileExcelOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { workActionsColumn } from '../../../utils/workTableHelpers';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import PermissionGate from '../../../components/PermissionGate';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useListReturnState } from '../../../hooks/useListReturnState';
import {
  buildWorkListFilters,
  getDefaultWorkDateRange,
  createWorkDateRangeFilter,
  applyWorkDateRangeChange,
} from '../../../utils/workFilterUtils';
import { buildCommonDatePresets } from '../../../utils/filterDateUtils';
import { toJalali } from '../../../utils/dateUtils';
import { PANEL_PRIMARY_BTN_CLASS, BULK_ACTION_BAR_CLASS, BULK_ACTION_BAR_TEXT_CLASS } from '../../../constants/ui';

const PROJECT_STATUS_OPTIONS = [
  { value: 'in progress', label: 'در حال انجام', color: 'processing' },
  { value: 'not started', label: 'شروع نشده', color: 'default' },
  { value: 'on hold', label: 'متوقف شده', color: 'warning' },
  { value: 'canceled', label: 'لغو شده', color: 'error' },
  { value: 'finished', label: 'تکمیل شده', color: 'success' },
  { value: 'در حال انجام', label: 'در حال انجام', color: 'processing' },
  { value: 'تکمیل شده', label: 'تکمیل شده', color: 'success' },
  { value: 'متوقف شده', label: 'متوقف شده', color: 'warning' },
];

const STATUS_SELECT_OPTIONS = [
  { value: 'in progress', label: 'در حال انجام' },
  { value: 'not started', label: 'شروع نشده' },
  { value: 'on hold', label: 'متوقف شده' },
  { value: 'canceled', label: 'لغو شده' },
  { value: 'finished', label: 'تکمیل شده' },
];

const getProjectStatusMeta = (status) =>
  PROJECT_STATUS_OPTIONS.find((o) => o.value === status) || {
    value: status,
    label: status || '—',
    color: 'default',
  };

const getProgressStrokeColor = (percent) => {
  const n = Number(percent) || 0;
  if (n >= 75) return '#52c41a';
  if (n >= 40) return '#1677ff';
  if (n >= 15) return '#faad14';
  return '#ff4d4f';
};

const Projects = () => {
  const navigate = useNavigate();
  const { canAdd, canEditRecord, canDelete, canDeleteRecord } = useResourcePermissions('projects');
  const defaultRange = useMemo(() => getDefaultWorkDateRange(), []);
  const [viewMode, setViewMode] = useState('list'); // list | pinned | archive | calendar
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarRange, setCalendarRange] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [total, setTotal] = useState(0);
  const [filterOptions, setFilterOptions] = useState({
    clients: [],
    categories: [],
    employees: [],
    teams: [],
  });
  const [filters, setFilters] = useState({
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
    status: 'all',
    progress: 'all',
    client_id: 'all',
    category_id: 'all',
    employee_id: 'all',
    team_id: 'all',
    pinned: 'all',
    public: 'all',
    date_filter_on: 'start_date',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [bulkStatusModal, setBulkStatusModal] = useState({ open: false, status: undefined });

  const captureCurrentListState = useCallback(
    () => ({
      filters,
      searchText,
      viewMode,
      pagination: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }),
    [filters, searchText, viewMode, pagination.current, pagination.pageSize],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
    if (restored.searchText != null) setSearchText(restored.searchText);
    if (restored.viewMode) setViewMode(restored.viewMode);
    if (restored.pagination) {
      setPagination((prev) => ({
        ...prev,
        current: restored.pagination.current || 1,
        pageSize: restored.pagination.pageSize || prev.pageSize,
      }));
    }
  });

  const label = (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>;

  const applyFilterOptions = (f) => {
    if (!f) return;
    setFilterOptions({
      clients: f.clients || [],
      categories: f.categories || [],
      employees: f.employees || [],
      teams: f.teams || f.departments || [],
    });
  };

  const fetchRows = async () => {
    if (viewMode === 'calendar') return;
    setLoading(true);
    try {
      const apiFilters = buildWorkListFilters(
        {
          ...filters,
          pinned: viewMode === 'pinned' ? 'pinned' : filters.pinned,
          page: pagination.current,
          per_page: pagination.pageSize,
        },
        searchText,
        defaultRange,
      );

      const res =
        viewMode === 'archive'
          ? await projectsApi.getArchive(apiFilters)
          : await projectsApi.getAll(apiFilters);

      const block = res?.data?.projects || res?.projects;
      setRows(block?.data || []);
      setTotal(block?.total ?? 0);
      applyFilterOptions(res?.data?.filters || res?.filters);
    } catch {
      message.error(viewMode === 'archive' ? 'خطا در دریافت بایگانی' : 'خطا در دریافت پروژه‌ها');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendar = useCallback(async () => {
    if (viewMode !== 'calendar' || !calendarRange?.start || !calendarRange?.end) return;
    setLoading(true);
    try {
      const res = await projectsApi.getCalendar({
        start: calendarRange.start,
        end: calendarRange.end,
        pinned: filters.pinned,
        team_id: filters.team_id,
        status: filters.status,
        employee_id: filters.employee_id,
        category_id: filters.category_id,
        client_id: filters.client_id,
        search: searchText || undefined,
        progress:
          filters.progress && filters.progress !== 'all'
            ? filters.progress
            : '0-20,21-40,41-60,61-80,81-99',
      });
      setCalendarEvents(res?.data?.events || res?.events || []);
      applyFilterOptions(res?.filters || res?.data?.filters);
    } catch {
      message.error('خطا در دریافت تقویم پروژه‌ها');
      setCalendarEvents([]);
    } finally {
      setLoading(false);
    }
  }, [viewMode, calendarRange, filters, searchText]);

  useEffect(() => {
    if (viewMode === 'calendar') return;
    if (!filters.startDate || !filters.endDate) return;
    const timer = setTimeout(() => {
      setPagination((p) => ({ ...p, current: 1 }));
      fetchRows();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, searchText, viewMode]);

  useEffect(() => {
    if (viewMode === 'calendar') return;
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, viewMode]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  const handleViewModeChange = (mode) => {
    setSelectedRowKeys([]);
    setViewMode(mode);
    setPagination((p) => ({ ...p, current: 1 }));
  };

  const handleBulkDelete = () => {
    if (!selectedRowKeys.length) {
      message.warning('حداقل یک پروژه انتخاب کنید');
      return;
    }
    confirmWorkDelete({
      title: 'حذف پروژه‌ها',
      content: `آیا از حذف ${selectedRowKeys.length} پروژه انتخاب‌شده اطمینان دارید؟`,
      onOk: async () => {
        setBulkLoading(true);
        try {
          await projectsApi.applyQuickAction('delete', selectedRowKeys);
          message.success('پروژه‌ها حذف شدند');
          setSelectedRowKeys([]);
          fetchRows();
        } catch {
          message.error('خطا در حذف گروهی');
        } finally {
          setBulkLoading(false);
        }
      },
    });
  };

  const handleBulkArchive = () => {
    if (!selectedRowKeys.length) {
      message.warning('حداقل یک پروژه انتخاب کنید');
      return;
    }
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>بایگانی پروژه‌ها</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          {selectedRowKeys.length} پروژه به بایگانی منتقل شود؟
        </span>
      ),
      okText: 'بایگانی',
      cancelText: 'انصراف',
      onOk: async () => {
        setBulkLoading(true);
        try {
          await Promise.all(selectedRowKeys.map((id) => projectsApi.archive(id)));
          message.success('پروژه‌ها بایگانی شدند');
          setSelectedRowKeys([]);
          fetchRows();
        } catch {
          message.error('خطا در بایگانی گروهی');
        } finally {
          setBulkLoading(false);
        }
      },
    });
  };

  const handleBulkStatus = async () => {
    if (!bulkStatusModal.status) {
      message.warning('وضعیت را انتخاب کنید');
      return;
    }
    setBulkLoading(true);
    try {
      await projectsApi.applyQuickAction('change-status', selectedRowKeys, {
        status: bulkStatusModal.status,
      });
      message.success('وضعیت پروژه‌ها به‌روز شد');
      setBulkStatusModal({ open: false, status: undefined });
      setSelectedRowKeys([]);
      fetchRows();
    } catch {
      message.error('خطا در تغییر وضعیت گروهی');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleRowDelete = (rowId) => {
    confirmWorkDelete({
      title: 'حذف پروژه',
      content: 'آیا از حذف این پروژه اطمینان دارید؟',
      onOk: async () => {
        try {
          await projectsApi.applyQuickAction('delete', [rowId]);
          message.success('پروژه حذف شد');
          fetchRows();
        } catch {
          message.error('خطا در حذف');
        }
      },
    });
  };

  const handlePinToggle = async (row) => {
    const pinned = Number(row.pinned_project) === 1;
    try {
      if (pinned) {
        await projectsApi.destroyPin(row.id);
        message.success('سنجاق برداشته شد');
      } else {
        await projectsApi.storePin(row.id);
        message.success('پروژه سنجاق شد');
      }
      fetchRows();
    } catch {
      message.error('خطا در تغییر سنجاق');
    }
  };

  const handleArchive = async (rowId) => {
    try {
      await projectsApi.archive(rowId);
      message.success('پروژه بایگانی شد');
      fetchRows();
    } catch {
      message.error('خطا در بایگانی');
    }
  };

  const handleRestore = async (rowId) => {
    try {
      await projectsApi.restoreArchive(rowId);
      message.success('پروژه از بایگانی بازگردانده شد');
      fetchRows();
    } catch {
      message.error('خطا در بازگردانی');
    }
  };

  const handleStatusChange = async (rowId, status) => {
    setStatusUpdatingId(rowId);
    try {
      await projectsApi.applyQuickAction('change-status', [rowId], { status });
      message.success('وضعیت پروژه به‌روز شد');
      fetchRows();
    } catch {
      message.error('خطا در تغییر وضعیت');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await projectsApi.exportList({
        ...filters,
        pinned: viewMode === 'pinned' ? 'pinned' : filters.pinned,
        search: searchText || undefined,
        page: pagination.current,
        per_page: pagination.pageSize,
      });
      message.success('خروجی اکسل در حال دانلود است');
    } catch (error) {
      message.error(error?.message || 'خطا در خروجی اکسل');
    } finally {
      setExporting(false);
    }
  };

  const bulkActionMenuItems = [
    {
      key: 'archive',
      label: label('بایگانی'),
      icon: <InboxOutlined />,
      onClick: handleBulkArchive,
    },
    {
      key: 'change-status',
      label: label('تغییر وضعیت'),
      icon: <EditOutlined />,
      onClick: () => setBulkStatusModal({ open: true, status: undefined }),
    },
    {
      key: 'delete',
      label: label('حذف'),
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleBulkDelete,
    },
  ];

  const getRowActionItems = (row) => {
    const pinned = Number(row.pinned_project) === 1;
    const isArchive = viewMode === 'archive';

    if (isArchive) {
      return [
        {
          key: 'restore',
          label: label('بازگردانی از بایگانی'),
          icon: <UndoOutlined />,
          onClick: () => handleRestore(row.id),
        },
        {
          key: 'view',
          label: label('مشاهده'),
          icon: <EyeOutlined />,
          onClick: () =>
            navigateToDetail(`/work/projects/${row.id}`, {}, {
              listPath: '/work/projects',
              listLabel: 'پروژه‌ها',
              entityLabel: row.project_name || `پروژه #${row.id}`,
            }),
        },
        { type: 'divider' },
        {
          key: 'delete',
          label: label('حذف'),
          icon: <DeleteOutlined />,
          danger: true,
          onClick: () => handleRowDelete(row.id),
        },
      ];
    }

    const items = [
      {
        key: 'view',
        label: label('مشاهده'),
        icon: <EyeOutlined />,
        onClick: () =>
          navigateToDetail(`/work/projects/${row.id}`, {}, {
            listPath: '/work/projects',
            listLabel: 'پروژه‌ها',
            entityLabel: row.project_name || `پروژه #${row.id}`,
          }),
      },
    ];

    if (!canEditRecord || canEditRecord(row)) {
      items.push({
        key: 'edit',
        label: label('ویرایش'),
        icon: <EditOutlined />,
        onClick: () => navigateToDetail(`/work/projects/${row.id}/edit`),
      });
    }

    items.push(
      {
        key: 'duplicate',
        label: label('ایجاد نسخه مشابه'),
        icon: <CopyOutlined />,
        onClick: () =>
          navigate('/work/projects/add', {
            state: { copyFromId: row.id, returnTo: '/work/projects' },
          }),
      },
      {
        key: 'gantt',
        label: label('نمودار گانت'),
        icon: <BarChartOutlined />,
        onClick: () =>
          navigateToDetail(`/work/projects/${row.id}?tab=overview`, {}, {
            listPath: '/work/projects',
            listLabel: 'پروژه‌ها',
            entityLabel: row.project_name || `پروژه #${row.id}`,
          }),
      },
      {
        key: 'pin',
        label: label(pinned ? 'برداشتن سنجاق' : 'سنجاق کردن پروژه'),
        icon: <PushpinOutlined />,
        onClick: () => handlePinToggle(row),
      },
      {
        key: 'archive',
        label: label('بایگانی'),
        icon: <InboxOutlined />,
        onClick: () => handleArchive(row.id),
      },
    );

    if (canDelete && (!canDeleteRecord || canDeleteRecord(row))) {
      items.push({ type: 'divider' });
      items.push({
        key: 'delete',
        label: label('حذف'),
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleRowDelete(row.id),
      });
    }

    return items;
  };

  const quickFilters = [
    {
      key: 'datePreset',
      type: 'datePreset',
      label: 'بازه سریع',
      placeholder: 'بازه سریع',
      options: buildCommonDatePresets(),
      onPreset: (range) => {
        setFilters((prev) => ({
          ...prev,
          startDate: range.startDate,
          endDate: range.endDate,
        }));
        setPagination((p) => ({ ...p, current: 1 }));
      },
    },
    createWorkDateRangeFilter(filters.startDate, filters.endDate),
    {
      key: 'status',
      type: 'select',
      label: 'وضعیت',
      value: filters.status === 'all' ? undefined : filters.status,
      options: STATUS_SELECT_OPTIONS,
    },
    {
      key: 'progress',
      type: 'select',
      label: 'در حال انجام',
      value: filters.progress === 'all' ? undefined : filters.progress,
      options: [
        { value: '0-20', label: '۰ تا ۲۰٪' },
        { value: '21-40', label: '۲۱ تا ۴۰٪' },
        { value: '41-60', label: '۴۱ تا ۶۰٪' },
        { value: '61-80', label: '۶۱ تا ۸۰٪' },
        { value: '81-100', label: '۸۱ تا ۱۰۰٪' },
      ],
    },
  ];

  const advancedFilters = [
    {
      key: 'client_id',
      type: 'select',
      label: 'مشتری',
      value: filters.client_id === 'all' ? undefined : filters.client_id,
      options: (filterOptions.clients || []).map((c) => ({ value: c.id, label: c.name })),
      showSearch: true,
    },
    {
      key: 'category_id',
      type: 'select',
      label: 'دسته',
      value: filters.category_id === 'all' ? undefined : filters.category_id,
      options: (filterOptions.categories || []).map((c) => ({
        value: c.id,
        label: c.category_name,
      })),
    },
    {
      key: 'employee_id',
      type: 'select',
      label: 'کارمند',
      value: filters.employee_id === 'all' ? undefined : filters.employee_id,
      options: (filterOptions.employees || []).map((e) => ({ value: e.id, label: e.name })),
      showSearch: true,
    },
    {
      key: 'date_filter_on',
      type: 'select',
      label: 'فیلتر تاریخ بر اساس',
      value: filters.date_filter_on,
      options: [
        { value: 'start_date', label: 'تاریخ شروع' },
        { value: 'deadline', label: 'ددلاین' },
      ],
    },
    {
      key: 'team_id',
      type: 'select',
      label: 'تیم',
      value: filters.team_id === 'all' ? undefined : filters.team_id,
      options: (filterOptions.teams || []).map((t) => ({
        value: t.id,
        label: t.team_name || t.name,
      })),
      showSearch: true,
    },
    viewMode === 'list'
      ? {
          key: 'pinned',
          type: 'select',
          label: 'پین شده',
          value: filters.pinned === 'all' ? undefined : filters.pinned,
          options: [
            { value: 'pinned', label: 'پین شده' },
            { value: 'unpinned', label: 'بدون پین' },
          ],
        }
      : null,
    {
      key: 'public',
      type: 'select',
      label: 'عمومی',
      value: filters.public === 'all' ? undefined : filters.public,
      options: [
        { value: 'public', label: 'عمومی' },
        { value: 'private', label: 'خصوصی' },
      ],
    },
  ].filter(Boolean);

  const columns = [
    {
      title: 'کد',
      dataIndex: 'project_short_code',
      width: 90,
      render: (t) => <span className="font-mono text-xs">{t || '—'}</span>,
    },
    {
      title: 'نام پروژه',
      dataIndex: 'project_name',
      ellipsis: true,
      render: (t, row) => (
        <button
          type="button"
          className="text-indigo-600 font-medium bg-transparent border-0 p-0 cursor-pointer"
          style={{ fontFamily: 'CustomFont' }}
          onClick={() =>
            navigateToDetail(`/work/projects/${row.id}`, {}, {
              listPath: '/work/projects',
              listLabel: 'پروژه‌ها',
              entityLabel: row.project_name || t || `پروژه #${row.id}`,
            })
          }
        >
          {t}
        </button>
      ),
    },
    {
      title: 'مشتری',
      key: 'client',
      render: (_, row) => (
        <span style={{ fontFamily: 'CustomFont' }}>{row.client?.name || '—'}</span>
      ),
    },
    {
      title: 'اعضای تیم',
      key: 'members',
      width: 140,
      render: (_, row) => {
        const members = Array.isArray(row.members) ? row.members : [];
        if (!members.length) return '—';
        return (
          <Avatar.Group max={{ count: 3 }} size="small">
            {members.map((m) => (
              <Tooltip key={m.id || m.user?.id} title={m.user?.name || '—'}>
                <Avatar src={m.user?.image_url}>{m.user?.name?.[0]}</Avatar>
              </Tooltip>
            ))}
          </Avatar.Group>
        );
      },
    },
    {
      title: 'تاریخ شروع',
      dataIndex: 'start_date',
      width: 110,
      render: (v) => (
        <span style={{ fontFamily: 'CustomFont' }}>{v ? toJalali(v) || '—' : '—'}</span>
      ),
    },
    {
      title: 'موعد نهایی',
      dataIndex: 'deadline',
      width: 110,
      render: (v) => (
        <span style={{ fontFamily: 'CustomFont' }}>{v ? toJalali(v) || '—' : '—'}</span>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      width: 150,
      render: (s, row) => {
        const meta = getProjectStatusMeta(s);
        if (viewMode === 'archive') {
          return <Tag color={meta.color}>{meta.label}</Tag>;
        }
        const canChange = !canEditRecord || canEditRecord(row);
        if (!canChange) {
          return <Tag color={meta.color}>{meta.label}</Tag>;
        }
        return (
          <Select
            size="small"
            value={s || undefined}
            style={{ width: '100%', fontFamily: 'CustomFont' }}
            loading={statusUpdatingId === row.id}
            onChange={(val) => handleStatusChange(row.id, val)}
            options={STATUS_SELECT_OPTIONS}
          />
        );
      },
    },
    {
      title: 'پیشرفت',
      dataIndex: 'completion_percent',
      width: 130,
      render: (p) => {
        const percent = Number(p) || 0;
        return (
          <Progress
            percent={percent}
            size="small"
            strokeColor={getProgressStrokeColor(percent)}
          />
        );
      },
    },
    workActionsColumn((_, row) => (
      <Dropdown menu={{ items: getRowActionItems(row) }} trigger={['click']}>
        <Button
          type="text"
          icon={<MoreOutlined />}
          onClick={(e) => e.stopPropagation()}
        />
      </Dropdown>
    )),
  ];

  const rowSelection =
    viewMode !== 'calendar' && viewMode !== 'archive' && canDelete
      ? {
          selectedRowKeys,
          onChange: setSelectedRowKeys,
          preserveSelectedRowKeys: true,
        }
      : viewMode === 'list' || viewMode === 'pinned'
        ? {
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            preserveSelectedRowKeys: true,
          }
        : undefined;

  const pageTitle =
    viewMode === 'archive'
      ? 'بایگانی پروژه‌ها'
      : viewMode === 'pinned'
        ? 'پروژه‌های سنجاق‌شده'
        : viewMode === 'calendar'
          ? 'تقویم پروژه‌ها'
          : 'پروژه‌ها';

  return (
    <WorkListPage
      title="پروژه‌ها"
      subtitle="مدیریت پروژه‌های سازمان"
      icon={ProjectOutlined}
      headerExtra={(
        <Space wrap>
          <Button
            icon={<FileExcelOutlined />}
            loading={exporting}
            onClick={handleExportExcel}
            style={{ fontFamily: 'CustomFont' }}
          >
            خروجی اکسل
          </Button>
          <Button
            icon={<CopyOutlined />}
            onClick={() => navigate('/work/projects/templates')}
            style={{ fontFamily: 'CustomFont' }}
          >
            الگوی پروژه
          </Button>
          <PermissionGate permission="add_project">
            {canAdd && viewMode !== 'archive' ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className={PANEL_PRIMARY_BTN_CLASS}
                onClick={() => navigate('/work/projects/add')}
                style={{ fontFamily: 'CustomFont' }}
              >
                پروژه جدید
              </Button>
            ) : null}
          </PermissionGate>
        </Space>
      )}
    >
      <div className="mb-4">
        <Segmented
          value={viewMode}
          onChange={handleViewModeChange}
          size="large"
          style={{ fontFamily: 'CustomFont' }}
          options={[
            { label: 'لیست', value: 'list', icon: <ProjectOutlined /> },
            { label: 'سنجاق‌شده', value: 'pinned', icon: <PushpinOutlined /> },
            { label: 'بایگانی', value: 'archive', icon: <InboxOutlined /> },
            { label: 'تقویم', value: 'calendar', icon: <CalendarOutlined /> },
          ]}
        />
      </div>

      <div
        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
        style={{ fontFamily: 'CustomFont' }}
      >
        {viewMode !== 'list' ? (
          <h2
            className="text-lg font-semibold m-0 mb-4 text-gray-800 dark:text-gray-100"
            style={{ fontFamily: 'CustomFont' }}
          >
            {pageTitle}
          </h2>
        ) : null}

        <StandardFilterPanel
          searchValue={searchText}
          onSearchChange={setSearchText}
          onSearch={() => {}}
          searchPlaceholder="جستجو پروژه..."
          quickFilters={viewMode === 'calendar' ? quickFilters.filter((f) => f.key !== 'datePreset' && f.key !== 'date_range') : quickFilters}
          advancedFilters={advancedFilters}
          onFilterChange={(k, v) => {
            if (k === 'date_range') {
              const next = applyWorkDateRangeChange(v, defaultRange);
              if (!next) return;
              setFilters((p) => ({ ...p, ...next }));
              return;
            }
            setFilters((p) => ({ ...p, [k]: v ?? 'all' }));
          }}
          onResetFilters={() => {
            setFilters({
              startDate: defaultRange.startDate,
              endDate: defaultRange.endDate,
              status: 'all',
              progress: 'all',
              client_id: 'all',
              category_id: 'all',
              employee_id: 'all',
              team_id: 'all',
              pinned: 'all',
              public: 'all',
              date_filter_on: 'start_date',
            });
            setSearchText('');
          }}
          totalCount={viewMode === 'calendar' ? calendarEvents.length : total}
          showAdvancedFilters
          showTotalCount
        />

        {selectedRowKeys.length > 0 && viewMode !== 'archive' && viewMode !== 'calendar' ? (
          <div className={BULK_ACTION_BAR_CLASS}>
            <span className={BULK_ACTION_BAR_TEXT_CLASS} style={{ fontFamily: 'CustomFont' }}>
              {selectedRowKeys.length} مورد انتخاب شده
            </span>
            <Dropdown menu={{ items: bulkActionMenuItems }} trigger={['click']} disabled={bulkLoading}>
              <Button
                type="primary"
                loading={bulkLoading}
                icon={<DownOutlined />}
                className={PANEL_PRIMARY_BTN_CLASS}
                style={{ fontFamily: 'CustomFont' }}
              >
                عملیات گروهی
              </Button>
            </Dropdown>
            <Button type="link" onClick={() => setSelectedRowKeys([])} style={{ fontFamily: 'CustomFont' }}>
              لغو انتخاب
            </Button>
          </div>
        ) : null}

        {viewMode === 'calendar' ? (
          <ProjectCalendarView
            events={calendarEvents}
            loading={loading}
            onRangeChange={(range) => {
              setCalendarRange((prev) =>
                prev?.start === range.start && prev?.end === range.end ? prev : range,
              );
            }}
            onEventClick={(ev) => {
              if (ev?.id) {
                navigateToDetail(`/work/projects/${ev.id}`, {}, {
                  listPath: '/work/projects',
                  listLabel: 'پروژه‌ها',
                  entityLabel: ev.title || ev.project_name || `پروژه #${ev.id}`,
                });
              }
            }}
          />
        ) : loading ? (
          <TableSkeleton rows={6} columns={8} />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={rows}
            rowSelection={rowSelection}
            scroll={{ x: 1100 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total,
              onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
            }}
            style={{ fontFamily: 'CustomFont' }}
          />
        )}
      </div>

      <Modal
        open={bulkStatusModal.open}
        title={<span style={{ fontFamily: 'CustomFont' }}>تغییر وضعیت گروهی</span>}
        onCancel={() => setBulkStatusModal({ open: false, status: undefined })}
        onOk={handleBulkStatus}
        confirmLoading={bulkLoading}
        okText="اعمال"
        cancelText="انصراف"
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
      >
        <div style={{ fontFamily: 'CustomFont' }}>
          <p className="mb-2">وضعیت جدید برای {selectedRowKeys.length} پروژه:</p>
          <Select
            className="w-full"
            placeholder="انتخاب وضعیت"
            value={bulkStatusModal.status}
            onChange={(status) => setBulkStatusModal((p) => ({ ...p, status }))}
            options={STATUS_SELECT_OPTIONS}
          />
        </div>
      </Modal>
    </WorkListPage>
  );
};

export default Projects;
